import { forwardRef, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LockSimple, Buildings, Translate, Paperclip, GitBranch, Gear } from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@libs/zapi-ui/shadcn";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { ProjectIconEditor } from "@/zapi-frontend/shared/ui/ProjectIconEditor";
import { ProjectDimensionControl } from "@/zapi-frontend/shared/ui/ProjectDimensionControl";
import { ProjectMetaFlagButton } from "@/zapi-frontend/features/FlagDeficiency";
import { CaseBriefVaultDrawer } from "@/zapi-frontend/widgets/CreateProjectChat/ui/CaseBriefVaultDrawer";
import type { TPreUploadedDocument } from "../model/usePreUploadDocuments";
import type { TOrganizationProjectDimension } from "@/zapi-frontend/shared/types/projects";
import { displayVersionName } from "@/zapi-frontend/shared/i18n/versionDisplayName";

function SetupRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <div className="flex items-center gap-[22px] py-4 w-full">
        <span className="w-[130px] shrink-0 text-[14px] leading-[13px] font-medium text-innoveraZinc-500">{label}</span>
        {children}
      </div>
      <div className="h-px w-full bg-innoveraZinc-150 last:hidden" />
    </>
  );
}

type TPillButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  icon?: React.ReactNode;
};

const PillButton = forwardRef<HTMLButtonElement, TPillButtonProps>(
  ({ icon, children, className, ...rest }, ref) => (
    <button
      ref={ref}
      type="button"
      {...rest}
      className={cn(
        "flex items-center gap-1 h-8 px-3 rounded-lg border border-innoveraZinc-150 bg-white",
        "text-[14px] leading-[13px] font-medium text-innoveraZinc-900 outline-hidden",
        !rest.disabled && "hover:border-innoveraZinc-200 transition-colors",
        className
      )}
    >
      {icon}
      {children}
    </button>
  )
);
PillButton.displayName = "PillButton";

type TLanguageOption = { label: string; value: string };

type TProjectSetupFormProps = {
  projectName: string;
  projectDescription: string;
  onProjectNameChange: (name: string) => void;
  onProjectDescriptionChange: (description: string) => void;
  iconShape: string | null;
  iconColor: string | null;
  logoUrl?: string | null;
  onPhotoChange: (logoBase64: string) => void | Promise<void>;
  visibility: "organization" | "private";
  onVisibilityChange: (visibility: "organization" | "private") => void;
  language: string;
  languageOptions: TLanguageOption[];
  onLanguageChange: (language: string) => void;
  vaultDocuments: TPreUploadedDocument[];
  vaultPendingUploadNames?: string[];
  onVaultAddFiles: (files: FileList) => void;
  onVaultAddWebLink: (url: string) => void;
  onVaultRemove: (documentId: number) => void;
  onInstructionsClick?: () => void;
  dimensions: TOrganizationProjectDimension[];
  dimensionValues: Record<string, string>;
  onDimensionChange: (dimensionId: number, value: string) => void;
  versionLabel?: string;
  readOnly?: boolean;
  caseBriefId?: number | null;
  projectId?: number | null;
};

export function ProjectSetupForm({
  projectName,
  projectDescription,
  onProjectNameChange,
  onProjectDescriptionChange,
  iconShape,
  iconColor,
  logoUrl,
  onPhotoChange,
  visibility,
  onVisibilityChange,
  language,
  languageOptions,
  onLanguageChange,
  vaultDocuments,
  vaultPendingUploadNames,
  onVaultAddFiles,
  onVaultAddWebLink,
  onVaultRemove,
  onInstructionsClick,
  dimensions,
  dimensionValues,
  onDimensionChange,
  versionLabel,
  readOnly = false,
  caseBriefId,
  projectId
}: TProjectSetupFormProps) {
  const { t } = useTranslation("projectHome");
  const [isVaultOpen, setIsVaultOpen] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = descriptionRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [projectDescription]);

  const languageName = (value: string, fallback?: string) => {
    if (value === "english") return t("projectSetup.languageName.english");
    if (value === "japanese") return t("projectSetup.languageName.japanese");
    return fallback ?? (value ? value.charAt(0).toUpperCase() + value.slice(1) : "");
  };
  const languageLabel = language
    ? languageName(language, languageOptions.find((option) => option.value === language)?.label)
    : t("projectSetup.languageName.english");
  const documentsCount = vaultDocuments.length;
  const canSwitchLanguage = languageOptions.length > 1 && !readOnly;

  return (
    <div className="flex-1 min-h-0 overflow-y-auto px-4">
      <div className="flex justify-center w-full">
        <div className="w-[720px] max-w-full py-10">
          <div className="flex flex-col gap-5 w-full">
            <div className="flex flex-col gap-3 w-full">
              <div className="flex flex-col gap-5 w-full">
                <ProjectIconEditor
                  shape={iconShape}
                  color={iconColor}
                  logoUrl={logoUrl}
                  onPhotoChange={onPhotoChange}
                  readOnly={readOnly}
                />
                <div className="group flex items-center gap-2 w-full">
                  <input
                    data-testid="project-setup-name-input"
                    ref={nameRef}
                    value={projectName}
                    onChange={(e) => onProjectNameChange(e.target.value)}
                    readOnly={readOnly}
                    placeholder={t("projectSetup.namePlaceholder")}
                    className="flex-1 min-w-0 text-[24px] leading-8 font-semibold text-innoveraZinc-900 outline-hidden bg-transparent placeholder:text-innoveraZinc-300"
                  />
                  <ProjectMetaFlagButton
                    caseBriefId={caseBriefId}
                    projectId={projectId}
                    fieldLabel="Name"
                    value={projectName}
                    fieldRef={nameRef}
                  />
                </div>
              </div>
              <div className="group flex items-start gap-2 w-full">
                <textarea
                  data-testid="project-setup-description-input"
                  ref={descriptionRef}
                  value={projectDescription}
                  onChange={(e) => onProjectDescriptionChange(e.target.value)}
                  readOnly={readOnly}
                  placeholder={t("projectSetup.descriptionPlaceholder")}
                  rows={1}
                  className="flex-1 min-w-0 resize-none text-[15px] leading-[23px] font-normal text-innoveraZinc-900 outline-hidden bg-transparent placeholder:text-innoveraZinc-300"
                />
                <ProjectMetaFlagButton
                  caseBriefId={caseBriefId}
                  projectId={projectId}
                  fieldLabel="Description"
                  value={projectDescription}
                  fieldRef={descriptionRef}
                />
              </div>
            </div>

            <div className="flex flex-col w-full">
              <SetupRow label={t("projectSetup.visibility")}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild disabled={readOnly}>
                    <PillButton
                      data-testid="project-setup-visibility-trigger"
                      icon={
                        visibility === "private" ? (
                          <LockSimple className="size-4" weight="regular" />
                        ) : (
                          <Buildings className="size-4" weight="regular" />
                        )
                      }
                    >
                      {visibility === "private" ? t("projectSetup.private") : t("projectSetup.organization")}
                    </PillButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuItem
                      data-testid="project-setup-visibility-organization-item"
                      onClick={() => onVisibilityChange("organization")}
                    >
                      {t("projectSetup.organization")}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid="project-setup-visibility-private-item"
                      onClick={() => onVisibilityChange("private")}
                    >
                      {t("projectSetup.private")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </SetupRow>

              <SetupRow label={t("projectSetup.language")}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild disabled={!canSwitchLanguage}>
                    <PillButton
                      data-testid="project-setup-language-trigger"
                      icon={<Translate className="size-4" weight="regular" />}
                    >
                      {languageLabel}
                    </PillButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    {languageOptions.map((option) => (
                      <DropdownMenuItem
                        key={option.value}
                        data-testid={`project-setup-language-${option.value}-item`}
                        onClick={() => onLanguageChange(option.value)}
                      >
                        {languageName(option.value, option.label)}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </SetupRow>

              <SetupRow label={t("projectSetup.version")}>
                <PillButton icon={<GitBranch className="size-4" weight="regular" />} disabled>
                  {displayVersionName(versionLabel) || t("projectSetup.versionInitial")}
                </PillButton>
              </SetupRow>

              <SetupRow label={t("projectSetup.vault")}>
                <PillButton
                  data-testid="project-setup-vault-button"
                  icon={<Paperclip className="size-4" weight="regular" />}
                  onClick={readOnly ? undefined : () => setIsVaultOpen(true)}
                  disabled={readOnly}
                >
                  <span className="flex items-center justify-center h-[18px] min-w-[21px] px-[5px] rounded bg-innoveraZinc-100 text-[14px] leading-[13px] font-medium text-innoveraZinc-900">
                    {documentsCount}
                  </span>
                  {documentsCount === 1 ? t("projectSetup.attachment") : t("projectSetup.attachments")}
                </PillButton>
              </SetupRow>

              {onInstructionsClick && (
                <SetupRow label={t("projectSetup.instructions")}>
                  <PillButton
                    data-testid="project-setup-instructions-button"
                    icon={<Gear className="size-4" weight="regular" />}
                    onClick={readOnly ? undefined : onInstructionsClick}
                    disabled={readOnly}
                  >
                    {t("projectSetup.instructions")}
                  </PillButton>
                </SetupRow>
              )}

              {dimensions.map((dimension) => (
                <SetupRow key={dimension.id} label={dimension.name}>
                  <ProjectDimensionControl
                    dimension={dimension}
                    value={dimensionValues[String(dimension.id)] ?? ""}
                    onChange={(value) => onDimensionChange(dimension.id, value)}
                    disabled={readOnly}
                  />
                </SetupRow>
              ))}
            </div>
          </div>
        </div>
      </div>

      {!readOnly && (
        <CaseBriefVaultDrawer
          open={isVaultOpen}
          onOpenChange={setIsVaultOpen}
          documents={vaultDocuments}
          pendingUploadNames={vaultPendingUploadNames}
          onAddFiles={onVaultAddFiles}
          onAddWebLink={onVaultAddWebLink}
          onRemove={onVaultRemove}
        />
      )}
    </div>
  );
}
